import { useState, useEffect, createContext, useContext } from 'react'
import PropTypes from 'prop-types'

import api from '../../../api/api'
import { isRoneResidencesBuilding, STORAGE_KEYS } from '../utils'
import { getFromStorage, setStorageValue } from '../../../helpers/helpers'

const CODE_REFRESH_INTERVAL = 12 * 60 * 60 * 1000 // 12 hours in milliseconds
const SETTINGS_REFRESH_INTERVAL = 60 * 60 * 1000 // 1 hour in milliseconds

const getWeeklyCode = () => {
  return getFromStorage(STORAGE_KEYS.WEEKLY_CODE, null)
}

const setWeeklyCode = (code) => {
  setStorageValue(STORAGE_KEYS.WEEKLY_CODE, code)
  setStorageValue(STORAGE_KEYS.WEEKLY_CODE_TIMESTAMP, Date.now().toString())
}

const getWeeklyCodeTimestamp = () => {
  return getFromStorage(STORAGE_KEYS.WEEKLY_CODE_TIMESTAMP, null)
}

const setWeeklyCodeTimestamp = (timestamp) => {
  setStorageValue(STORAGE_KEYS.WEEKLY_CODE_TIMESTAMP, timestamp)
}

const getCachedResidents = () => {
  return getFromStorage(STORAGE_KEYS.RESIDENTS, [])
}

const cacheResidents = (residents) => {
  setStorageValue(STORAGE_KEYS.RESIDENTS, residents)
}

const getCachedSettings = () => {
  return getFromStorage(STORAGE_KEYS.SETTINGS, null)
}

const cacheSettings = (settings) => {
  setStorageValue(STORAGE_KEYS.SETTINGS, settings)
  setStorageValue(STORAGE_KEYS.SETTINGS_TIMESTAMP, Date.now().toString())
}

const getSettingsTimestamp = () => {
  return getFromStorage(STORAGE_KEYS.SETTINGS_TIMESTAMP, null)
}

export const getPackageSessions = () => {
  return getFromStorage(STORAGE_KEYS.PACKAGE_SESSIONS, [])
}

export const removePackageSessions = () => {
  setStorageValue(STORAGE_KEYS.PACKAGE_SESSIONS, [])
}

const createStoragePackageSession = ({ packages, startTime }) => {
  const sessionId = `session_${Date.now()}`
  const newSession = {
    id: sessionId,
    startTime,
    endTime: Date.now(),
    packages: packages || [],
  }

  const currentSessions = getPackageSessions()
  setStorageValue(STORAGE_KEYS.PACKAGE_SESSIONS, [...currentSessions, newSession])
  return sessionId
}

export const useOfflineMode = () => {
  const { isOffline, hasBadConnection, setIsOffline, setHasBadConnection } =
    useContext(OfflineModeContext)

  const [cachedSettings, setCachedSettings] = useState(() => getCachedSettings())

  useEffect(() => {
    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const fetchWeeklyCode = async () => {
    if (isOffline) return

    try {
      const response = await api.getPackageRoomWeeklyCode()
      if (response.data && response.data.accessCode) {
        setWeeklyCode(response.data.accessCode)
        setWeeklyCodeTimestamp(Date.now())
      }
    } catch (error) {
      console.error('Error fetching weekly code:', error)
    }
  }

  const fetchSettings = async () => {
    if (isOffline) return

    try {
      const response = await api.getPackageRoomSettings()
      if (response.data) {
        cacheSettings(response.data)
        setCachedSettings(response.data)
      }
    } catch (error) {
      console.error('Error fetching package room settings:', error)
    }
  }

  useEffect(() => {
    const checkAndRefreshCode = () => {
      try {
        const timestamp = getWeeklyCodeTimestamp()
        const now = Date.now()

        if (!timestamp || now - parseInt(timestamp) > CODE_REFRESH_INTERVAL) {
          fetchWeeklyCode()
        }
      } catch (error) {
        console.error('Error checking code timestamp:', error)
      }
    }

    checkAndRefreshCode()
    const interval = setInterval(checkAndRefreshCode, CODE_REFRESH_INTERVAL)

    return () => clearInterval(interval)
  }, [isOffline])

  useEffect(() => {
    const checkAndRefreshSettings = () => {
      try {
        const timestamp = getSettingsTimestamp()
        const now = Date.now()

        if (!timestamp || now - parseInt(timestamp) > SETTINGS_REFRESH_INTERVAL) {
          fetchSettings()
        }
      } catch (error) {
        console.error('Error checking settings timestamp:', error)
      }
    }

    checkAndRefreshSettings()
    const interval = setInterval(checkAndRefreshSettings, SETTINGS_REFRESH_INTERVAL)

    return () => clearInterval(interval)
  }, [isOffline])

  const handleCacheResidents = (residents) => {
    cacheResidents(residents)
  }

  const handleCreatePackagesSession = ({ packages, startTime }) => {
    return createStoragePackageSession({ packages, startTime })
  }

  return {
    isOffline,
    hasBadConnection,
    setHasBadConnection,
    isConnectionIssue: isOffline || hasBadConnection,
    weeklyCode: getWeeklyCode(),
    cachedResidents: getCachedResidents(),
    cachedSettings,
    packageSessions: getPackageSessions(),
    handleCacheResidents,
    handleCreatePackagesSession,
    // Note: For Rone Residences we have special handling for package room. Based on this property the logic is different.
    isRoneResidencesBuilding: isRoneResidencesBuilding(cachedSettings?.building?.name),
  }
}

const OfflineModeContext = createContext(null)

export const OfflineModeProvider = ({ children }) => {
  const [isOffline, setIsOffline] = useState(false)
  const [hasBadConnection, setHasBadConnection] = useState(false)

  return (
    <OfflineModeContext.Provider
      value={{ isOffline, hasBadConnection, setIsOffline, setHasBadConnection }}
    >
      {children}
    </OfflineModeContext.Provider>
  )
}

OfflineModeProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
